import React, { useRef, useState } from "react";
import { Autocomplete } from "@react-google-maps/api";

type SearchResult = google.maps.places.Autocomplete | undefined;

interface Props {
  children: React.ReactNode & React.ReactChild;
  searchResult: SearchResult;
  setSearchResult: React.Dispatch<React.SetStateAction<SearchResult>>;
  onPlaceSelect: (searchResult: google.maps.places.Autocomplete, placeResult?: google.maps.places.PlaceResult) => void;
}

const GoogleSearchAutocompleteCity: React.FC<Props> = ({ children, searchResult, setSearchResult, onPlaceSelect }) => {
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  function onLoadAutocomplete(autocomplete: SearchResult) {
    setSearchResult(autocomplete);
  }

  function placeChangeHandler() {
    if (!searchResult) return;

    const place = searchResult.getPlace();

    // Normal selection with complete data
    if (place?.geometry?.location) {
      onPlaceSelect(searchResult, place);
      return;
    }

    // Fallback for Enter key press - fetch complete place details
    const searchQuery = inputValue.trim() || place?.name || "";
    if (!searchQuery) return;

    const autocompleteService = new google.maps.places.AutocompleteService();

    autocompleteService.getPlacePredictions({ input: searchQuery }, (predictions, status) => {
      if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions?.length) {
        return;
      }

      const placesService = new google.maps.places.PlacesService(document.createElement("div"));

      placesService.getDetails(
        {
          placeId: predictions[0].place_id || "",
          fields: ["geometry", "name", "place_id"],
        },
        (placeResult, detailsStatus) => {
          if (detailsStatus === google.maps.places.PlacesServiceStatus.OK && placeResult?.geometry?.location) {
            onPlaceSelect(searchResult, placeResult);
          }
        }
      );
    });
  }

  if (!children) return null;

  const childElement = children as React.ReactElement<any>;
  const { onChange, onKeyDown, inputRef: childInputRef } = childElement.props;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    onChange?.(e);
  };

  const handleInputRef = (node: any) => {
    if (node instanceof HTMLInputElement) {
      inputRef.current = node;
    } else if (node?.querySelector) {
      inputRef.current = node.querySelector("input");
    }

    if (typeof childInputRef === "function") {
      childInputRef(node);
    } else if (childInputRef) {
      childInputRef.current = node;
    }
  };

  const enhancedChild = React.cloneElement(childElement, {
    onChange: handleChange,
    onKeyDown,
    inputRef: handleInputRef,
    ref: handleInputRef,
  });

  return (
    <Autocomplete
      onPlaceChanged={placeChangeHandler}
      onLoad={onLoadAutocomplete}
      options={{
        componentRestrictions: { country: "fr" },
        types: ["(cities)"],
        fields: ["address_components", "geometry", "name", "place_id"],
      }}>
      {enhancedChild}
    </Autocomplete>
  );
};

export default GoogleSearchAutocompleteCity;
